import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { shared } from '../../styles/shared.js';
import { LocaleController } from '../../i18n/locale-controller.js';
import { t } from '../../i18n/t.js';
import { generateAdditionTrial, type AdditionTrial } from '../../distractors/addition.js';
import { pickRandom } from '../../distractors/random.js';
import { getProgression } from '../../progression/context.js';
import { getAudioService } from '../../audio/context.js';
import { TRIALS_PER_SESSION, STREAK_THRESHOLD } from '../../session/config.js';
import type { SessionResult } from '../../progression/progression.js';
import { iconUrl, TRIAL_ICONS, type IconAsset } from '../../assets/icons.js';
import '../../components/digit-tile.js';
import '../../components/rewards-overlay.js';
import '../../components/streak-celebration.js';
import '../../components/session-summary.js';

type Phase = 'playing' | 'summary';

const FEEDBACK_DELAY_MS = 1000;
/** Beat to hold the demo-counted state after the last digit before advancing. */
const POST_AUDIO_HOLD_MS = 700;

@customElement('add-game')
export class AddGame extends LitElement {
  static override styles = [
    shared,
    css`
      :host {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--space-5);
        height: 100%;
        padding: var(--space-5);
      }

      .prompt {
        font-family: var(--font-display);
        font-size: var(--type-l);
        text-align: center;
        margin: 0;
      }

      .equation {
        display: flex;
        align-items: center;
        gap: var(--space-5);
        flex-wrap: wrap;
        justify-content: center;
      }

      .group {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
        justify-content: center;
        max-width: 380px;
        min-width: 120px;
        min-height: 120px;
        padding: var(--space-3);
        border-radius: var(--radius-lg);
        background: rgba(255, 255, 255, 0.6);
        align-items: center;
      }

      .group--empty {
        background: transparent;
        border: 3px dashed var(--color-fg-subtle);
      }

      .group img {
        width: 56px;
        height: 56px;
        transition:
          opacity var(--motion-medium) var(--easing-standard),
          transform var(--motion-medium) var(--easing-standard);
      }

      .group img.demo-counted {
        opacity: 0.4;
        transform: scale(0.9);
      }

      .op {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: var(--type-xxl);
        color: var(--color-fg);
      }

      .tiles {
        display: flex;
        gap: var(--space-5);
        justify-content: center;
        flex-wrap: wrap;
      }

      .progress {
        position: fixed;
        top: var(--space-3);
        right: var(--space-3);
        font-family: var(--font-display);
        font-weight: 700;
        font-size: var(--type-m);
        background: var(--color-surface);
        padding: var(--space-2) var(--space-4);
        border-radius: var(--radius-pill);
        box-shadow: var(--shadow-tile);
      }

      @media (max-height: 500px) {
        :host {
          gap: var(--space-2);
          padding: var(--space-3);
        }

        .prompt {
          font-size: var(--type-m);
        }

        .equation {
          gap: var(--space-3);
        }

        .group {
          min-width: 80px;
          min-height: 80px;
          padding: var(--space-2);
          gap: var(--space-1);
        }

        .group img {
          width: 36px;
          height: 36px;
        }

        .op {
          font-size: var(--type-l);
        }

        .tiles {
          gap: var(--space-3);
        }

        .progress {
          font-size: var(--type-s);
          padding: var(--space-1) var(--space-3);
        }
      }
    `,
  ];

  @state() private phase: Phase = 'playing';
  @state() private trial: AdditionTrial | null = null;
  @state() private trialIndex = 0;
  @state() private correctCount = 0;
  @state() private streak = 0;
  @state() private rewardShow = false;
  @state() private streakShow = false;
  @state() private locked = false;
  @state() private flashedTile: number | null = null;
  /** Number of objects (A union B, in display order) shown as demo-counted. */
  @state() private demoCount = 0;
  @state() private sessionResult: SessionResult | null = null;
  @state() private sessionIcon: IconAsset = TRIAL_ICONS[0]!;

  private locale = new LocaleController(this);

  override connectedCallback(): void {
    super.connectedCallback();
    this.sessionIcon = pickRandom(TRIAL_ICONS, Math.random);
    void this.preloadSessionAudio();
    this.startNextTrial();
  }

  private async preloadSessionAudio(): Promise<void> {
    const params = getProgression().additionParams();
    const keys = ['prompt.total', 'feedback.correct', 'feedback.tryagain'];
    for (let n = 0; n <= params.sumCap; n++) keys.push(`digit.${n}`);
    await getAudioService().preload(keys);
  }

  private startNextTrial(): void {
    const params = getProgression().additionParams();
    this.trial = generateAdditionTrial(params);
    this.locked = false;
    this.flashedTile = null;
    this.demoCount = 0;
    void getAudioService().play('prompt.total', 'prompt');
  }

  private async onTileTap(value: number): Promise<void> {
    if (this.locked || this.trial === null) return;
    if (value === this.trial.sum) {
      await this.handleCorrect();
    } else {
      await this.handleWrong();
    }
  }

  private async handleCorrect(): Promise<void> {
    if (this.trial === null) return;
    this.locked = true;
    this.flashedTile = this.trial.sum;
    this.correctCount++;
    this.streak++;
    void getAudioService().play('feedback.correct', 'reward');
    this.rewardShow = true;
    if (this.streak >= STREAK_THRESHOLD) this.streakShow = true;
    const progression = getProgression();
    await progression.recordAttempt('addition', progression.getLevel('addition'), true);
    window.setTimeout(() => this.advance(), FEEDBACK_DELAY_MS);
  }

  private async handleWrong(): Promise<void> {
    if (this.trial === null) return;
    this.locked = true;
    this.streak = 0;
    const progression = getProgression();
    await progression.recordAttempt('addition', progression.getLevel('addition'), false);

    const audio = getAudioService();
    await audio.play('feedback.tryagain', 'reward');

    // Demo-count the union: walk through both groups one item at a time
    // synced to each digit's audio.
    this.demoCount = 0;
    for (let i = 1; i <= this.trial.sum; i++) {
      this.demoCount = i;
      await audio.play(`digit.${i}`, 'reward');
    }
    window.setTimeout(() => this.advance(), POST_AUDIO_HOLD_MS);
  }

  private advance(): void {
    this.trialIndex++;
    if (this.trialIndex >= TRIALS_PER_SESSION) {
      void this.finishSession();
      return;
    }
    this.startNextTrial();
  }

  private async finishSession(): Promise<void> {
    const result = await getProgression().recordSessionResult('addition', this.correctCount);
    this.sessionResult = result;
    this.phase = 'summary';
  }

  private renderGroup(count: number, startIndex: number): unknown {
    const items: number[] = [];
    for (let i = 0; i < count; i++) items.push(i);
    if (count === 0) {
      return html`<div class="group group--empty" data-testid="group-empty"></div>`;
    }
    return html`<div class="group" data-testid="group">
      ${items.map((i) => {
        const isCounted = startIndex + i < this.demoCount;
        return html`<img
          class=${isCounted ? 'demo-counted' : ''}
          src=${iconUrl(this.sessionIcon)}
          alt=${this.sessionIcon.labelEn}
        />`;
      })}
    </div>`;
  }

  override render() {
    if (this.phase === 'summary' && this.sessionResult) {
      return html`<session-summary
        .starsEarned=${this.correctCount}
        .promoted=${this.sessionResult.promoted}
        .demoted=${this.sessionResult.demoted}
      ></session-summary>`;
    }
    if (this.trial === null) return html``;

    return html`<div class="progress" data-testid="progress">
        ${this.trialIndex + 1} / ${TRIALS_PER_SESSION}
      </div>
      <h2 class="prompt">${t(this.locale.locale, 'prompt.total')}</h2>
      <div class="equation">
        ${this.renderGroup(this.trial.a, 0)}
        <span class="op" aria-hidden="true">+</span>
        ${this.renderGroup(this.trial.b, this.trial.a)}
      </div>
      <div class="tiles">
        ${this.trial.tiles.map(
          (v) =>
            html`<digit-tile
              variant="digit"
              .value=${v}
              ?disabled=${this.locked && this.flashedTile !== v}
              ?flash=${this.flashedTile === v}
              data-testid="add-tile"
              data-correct=${v === this.trial?.sum ? 'true' : 'false'}
              @tile-tap=${(e: CustomEvent<{ value: number | null }>) =>
                this.onTileTap(e.detail.value!)}
            ></digit-tile>`,
        )}
      </div>
      <rewards-overlay
        .show=${this.rewardShow}
        @reward-done=${() => (this.rewardShow = false)}
      ></rewards-overlay>
      <streak-celebration
        .show=${this.streakShow}
        @streak-done=${() => (this.streakShow = false)}
      ></streak-celebration>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'add-game': AddGame;
  }
}
